import { useState, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useApiFetch, ApiError } from '../lib/api'
import { useAuth } from '../auth/useAuth'
import { Spinner } from '../components/Spinner'
import { Avatar } from '../components/Avatar'
import { InfoIcon } from '../components/icons/InfoIcon'
import { InfoTooltip } from '../components/InfoTooltip'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FilterEraser } from '../components/FilterEraser'
import { MultiSelectFilter } from '../components/MultiSelectFilter'
import { DateRangeFilter, type DateRangeValue } from '../components/DateRangeFilter'
import { AuditDiffViewer } from '../components/AuditDiffViewer'
import { DetailDrawer, DrawerField } from '../components/DetailDrawer'
import { Pagination, DEFAULT_PAGE_SIZE_OPTIONS } from '../components/Pagination'
import { SortableTh, toggleSort, type SortState } from '../components/mcp/analytics/SortableTh'
import { PageHeader } from '../components/PageHeader'
import { FEATURES } from '../lib/features'
import type {
  AuditLogActorsResponse,
  AuditLogEntry,
  AuditLogListResponse,
  AuditLogStats,
  SparkListResponse,
} from '../lib/types'

/* ─── Constants ─── */

const SCOPES = ['Agent', 'Global', 'Skill', 'Connection', 'Source', 'Model'] as const
const ACTIONS = ['Created', 'Updated', 'Deleted'] as const

const PERIOD_DEFS = [
  { key: '24h', days: 1, labelKey: 'auditLog.dateRanges.last24h' },
  { key: '7d', days: 7, labelKey: 'auditLog.dateRanges.last7d' },
  { key: '30d', days: 30, labelKey: 'auditLog.dateRanges.last30d' },
  { key: '90d', days: 90, labelKey: 'auditLog.dateRanges.last90d' },
] as const
const DEFAULT_PERIOD_KEY = '30d'

const DEFAULT_PAGE_SIZE = 10

/* `width` is load-bearing: without it the table auto-lays out and Section and
   Change give up their share as the content changes between pages. The severity
   and details columns carry theirs inline on their own `<th>`s (2% + 5%). */
const COLUMNS = [
  { key: 'timestamp', labelKey: 'auditLog.table.timestamp', descendingFirst: true, width: '11%' },
  { key: 'user', labelKey: 'auditLog.table.user', descendingFirst: false, width: '11%' },
  { key: 'scope', labelKey: 'auditLog.table.scope', descendingFirst: false, width: '9%' },
  { key: 'agent', labelKey: 'auditLog.table.spark', descendingFirst: false, width: '6%' },
  { key: 'section', labelKey: 'auditLog.table.section', descendingFirst: false, width: '20%' },
  { key: 'action', labelKey: 'auditLog.table.action', descendingFirst: false, width: '7%' },
  { key: 'change', labelKey: 'auditLog.table.change', descendingFirst: false, width: '29%' },
] as const

const SORT_COLS: readonly string[] = COLUMNS.map(c => c.key)

const DEFAULT_SORT: SortState = { col: 'timestamp', dir: 'desc' }

/* ─── Helpers ─── */

/** The URL's sort, or `null` when it names a column this table does not render or
 *  a direction the API would reject — in which case the caller falls back to the
 *  default rather than sending a 400 the pager renders no way out of. */
function readSort(params: URLSearchParams): SortState | null {
  const col = params.get('sortCol')
  const dir = params.get('sortDir')
  if (!col || !SORT_COLS.includes(col)) return null
  if (dir !== 'asc' && dir !== 'desc') return null
  return { col, dir }
}

/** The URL's page size, restricted to the sizes the pager actually offers. */
function readPageSize(params: URLSearchParams): number {
  const raw = parseInt(params.get('pageSize') ?? '', 10)
  return DEFAULT_PAGE_SIZE_OPTIONS.includes(raw) ? raw : DEFAULT_PAGE_SIZE
}

function getFromDate(range: string): string {
  const d = new Date()
  if (range === '24h') d.setHours(d.getHours() - 24)
  else if (range === '7d') d.setDate(d.getDate() - 7)
  else d.setDate(d.getDate() - 30) // '30d' and any unrecognised value
  return d.toISOString()
}

function scopeI18nKey(scope: string) {
  switch (scope) {
    case 'Agent': return 'auditLog.scopes.agent' as const
    case 'Global': return 'auditLog.scopes.global' as const
    case 'Skill': return 'auditLog.scopes.skill' as const
    case 'Connection': return 'auditLog.scopes.connection' as const
    case 'Source': return 'auditLog.scopes.source' as const
    default: return 'auditLog.scopes.model' as const
  }
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function is403(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403
}

function normalizeAction(action: string): 'created' | 'updated' | 'deleted' {
  const lower = action.toLowerCase()
  if (lower.includes('creat')) return 'created'
  if (lower.includes('delet')) return 'deleted'
  return 'updated'
}

/* ─── Scope badge ─── */

const SCOPE_TO_CLASS: Record<string, string> = {
  Agent:      'badge-mambu',
  Global:     'badge-global',
  Skill:      'badge-skill',
  Connection: 'badge-connection',
  Source:     'badge-source',
  Model:      'badge-model',
}

function ScopeBadge({ scope }: { scope: string }) {
  const cls = SCOPE_TO_CLASS[scope] ?? 'badge-model'
  return (
    <span className={`badge ${cls}`}>
      {scope}
    </span>
  )
}

/* ─── Action badge ─── */

function ActionBadge({ action }: { action: string }) {
  const key = normalizeAction(action)
  return (
    <span className={`audit-action ${key}`}>
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </span>
  )
}

/* ─── Severity dot ─── */

const SEVERITY_COLOR: Record<string, string> = {
  low:    'var(--accent)',
  medium: 'var(--warning)',
  high:   'var(--danger)',
}

function SeverityDot({ severity }: { severity: string }) {
  const { t } = useTranslation()
  const key = severity === 'low' || severity === 'medium' || severity === 'high' ? severity : 'low'
  return (
    <span
      aria-label={t('auditLog.severityAria', { severity: t(`auditLog.severityLabel.${key}`) })}
      title={`${t('auditLog.severityLegend.title')}: ${t(`auditLog.severityLegend.${key}`)}`}
      style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
        background: SEVERITY_COLOR[severity] ?? SEVERITY_COLOR.low, flexShrink: 0,
      }}
    />
  )
}

/* ─── Actor cell ─── */

function ActorCell({ entry }: { entry: AuditLogEntry }) {
  const name = entry.actorDisplayName ?? entry.actorSub
  return (
    <div className="audit-user">
      <Avatar className="audit-avatar audit-avatar-user" size={13} />
      <span style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{name}</span>
    </div>
  )
}

/* ─── Detail drawer ───
   The shell (backdrop, drag handle, Escape, focus) is `components/DetailDrawer`,
   shared with the MCP Analytics Search Activity slide-over. Only the body is
   audit-specific. */

interface DrawerProps {
  entry: AuditLogEntry | null
  onClose: () => void
}

function AuditDetailDrawer({ entry, onClose }: DrawerProps) {
  const { t } = useTranslation()
  return (
    <DetailDrawer
      isOpen={entry !== null}
      title={t('auditLog.drawer.title')}
      onClose={onClose}
      recordKey={entry?.id ?? null}
      testId="audit-detail-drawer"
    >
      {entry && (
        <div style={{
          flex: 1, minHeight: 0, overflow: 'hidden', padding: '16px 20px 24px', fontSize: 13,
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Fixed fields: meta row, resource, section, change */}
          <div style={{ flexShrink: 0 }}>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
              marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border-light)',
            }}>
              <ScopeBadge scope={entry.resourceType} />
              <ActionBadge action={entry.action} />
              <ActorCell entry={entry} />
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 'auto' }}>
                {formatTimestamp(entry.occurredAt)}
              </span>
            </div>

            {entry.resourceDisplayName && (
              <DrawerField label={t('auditLog.drawer.resource')} value={entry.resourceDisplayName} />
            )}
            {entry.section && (
              <DrawerField label={t('auditLog.drawer.section')} value={entry.section} mono />
            )}
            <DrawerField label={t('auditLog.drawer.change')} value={entry.changeDescription} />
          </div>

          {/* Side-by-side changes: split diff of the before/after configuration.
              This is the only part of the drawer body that scrolls internally —
              it must never grow the drawer past the pane height. */}
          <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{
              fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 8, flexShrink: 0,
            }}>
              {t('auditLog.drawer.changes')}
            </div>
            <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
              <AuditDiffViewer key={entry.id} beforeState={entry.beforeState} afterState={entry.afterState} />
            </div>
          </div>
        </div>
      )}
    </DetailDrawer>
  )
}

/* ─── Summary KPI tile ─── */

function KpiTile({ value, label }: { value: string; label: string }) {
  return (
    <div className="kpi">
      <div className="kpi-val">{value}</div>
      <div className="kpi-lbl">{label}</div>
    </div>
  )
}

/* ─── Severity info tooltip (header icon) ─── */

function SeverityInfoIcon() {
  const { t } = useTranslation()
  const items: Array<{ severity: string; label: string }> = [
    { severity: 'low',    label: t('auditLog.severityLegend.low') },
    { severity: 'medium', label: t('auditLog.severityLegend.medium') },
    { severity: 'high',   label: t('auditLog.severityLegend.high') },
  ]
  return (
    <InfoTooltip
      width={200}
      color="var(--text-tertiary)"
      icon={
        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="6" cy="6" r="5" stroke="var(--text-tertiary)" strokeWidth="1.5" fill="none" />
        </svg>
      }
    >
      <strong>{t('auditLog.severityLegend.title')}</strong>
      <br />
      {items.map(({ severity, label }) => (
        <span key={severity} style={{ display: 'block', marginTop: 2 }}>
          <SeverityDot severity={severity} /> {label}
        </span>
      ))}
    </InfoTooltip>
  )
}

/* ─── Main component ─── */

export function AuditLog() {
  const apiFetch = useApiFetch()
  const { isAuthenticated, isGlobalAdmin } = useAuth()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'configuration' | 'interactions'>('configuration')
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null)
  const [showExportWarning, setShowExportWarning] = useState(false)
  const [snapshotLoading, setSnapshotLoading] = useState(false)

  /* ─── Filter state from URL ─── */
  const scope = searchParams.getAll('scope')
  const sparkId = searchParams.getAll('agentId')
  const action = searchParams.getAll('action')
  const actorSub = searchParams.getAll('actorSub')
  const dateRange = searchParams.get('dateRange') ?? DEFAULT_PERIOD_KEY
  const dateFrom = searchParams.get('dateFrom') ?? ''
  const dateTo = searchParams.get('dateTo') ?? ''
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1)
  const search = searchParams.get('search') ?? ''

  /* Sort and page size live in the URL with every other part of the view, so a
     sorted, resized table can be reloaded and shared. Both are re-validated
     against the columns this page renders and the sizes it offers: the values go
     straight into a query string the API rejects with a 400, and a hand-edited
     or stale URL must not be able to take the table down. */
  const sort: SortState = readSort(searchParams) ?? DEFAULT_SORT
  const pageSize = readPageSize(searchParams)

  function setFilter(key: string, value: string) {
    setSearchParams(p => {
      const next = new URLSearchParams(p)
      if (value) next.set(key, value)
      else next.delete(key)
      if (key !== 'page') next.delete('page')
      return next
    }, { replace: true })
  }

  function setPage(nextPage: number) {
    setSearchParams(p => {
      const next = new URLSearchParams(p)
      if (nextPage > 1) next.set('page', String(nextPage))
      else next.delete('page')
      return next
    }, { replace: true })
  }

  /* Any sort or page-size change is a new question, so it starts at page 1. */
  function setSort(next: SortState) {
    setSearchParams(p => {
      const params = new URLSearchParams(p)
      if (next.col === DEFAULT_SORT.col && next.dir === DEFAULT_SORT.dir) {
        params.delete('sortCol')
        params.delete('sortDir')
      } else {
        params.set('sortCol', next.col)
        params.set('sortDir', next.dir)
      }
      params.delete('page')
      return params
    }, { replace: true })
  }

  function handleSort(colKey: string) {
    const column = COLUMNS.find(c => c.key === colKey)
    setSort(toggleSort(sort, colKey, column?.descendingFirst ?? false))
  }

  function handlePageSizeChange(size: number) {
    setSearchParams(p => {
      const params = new URLSearchParams(p)
      if (size === DEFAULT_PAGE_SIZE) params.delete('pageSize')
      else params.set('pageSize', String(size))
      params.delete('page')
      return params
    }, { replace: true })
  }

  function setMultiFilter(key: string, values: string[]) {
    setSearchParams(p => {
      const next = new URLSearchParams(p)
      next.delete(key)
      values.forEach(v => next.append(key, v))
      next.delete('page')
      return next
    }, { replace: true })
  }

  function setDateRange(next: DateRangeValue) {
    setSearchParams(p => {
      const params = new URLSearchParams(p)
      const period = next.periodLabel ? periodKeyByLabel[next.periodLabel] : undefined
      if (period) {
        params.set('dateRange', period)
        params.delete('dateFrom')
        params.delete('dateTo')
      } else if (next.start && next.end) {
        params.set('dateRange', 'custom')
        params.set('dateFrom', next.start)
        params.set('dateTo', next.end)
      }
      params.delete('page')
      return params
    }, { replace: true })
  }

  /* ─── Period options (Period filter offers no window longer than the 90-day retention) ─── */
  const periodOptions = PERIOD_DEFS.map(p => ({ key: p.key, days: p.days, label: t(p.labelKey) }))
  const periodLabelByKey = Object.fromEntries(periodOptions.map(p => [p.key, p.label]))
  const periodKeyByLabel = Object.fromEntries(periodOptions.map(p => [p.label, p.key]))
  const dateRangeValue: DateRangeValue = dateRange === 'custom'
    ? { start: dateFrom || null, end: dateTo || null, periodLabel: null }
    : { start: null, end: null, periodLabel: periodLabelByKey[dateRange] ?? periodLabelByKey[DEFAULT_PERIOD_KEY] }

  /* ─── API query params ─── */
  const apiParams = useMemo(() => {
    const p = new URLSearchParams()
    scope.forEach(v => p.append('scope', v))
    sparkId.forEach(v => p.append('agentId', v))
    action.forEach(v => p.append('action', v))
    actorSub.forEach(v => p.append('actorSub', v))
    if (dateRange === 'custom' && dateFrom) {
      p.set('from', new Date(dateFrom).toISOString())
      if (dateTo) p.set('to', new Date(`${dateTo}T23:59:59.999`).toISOString())
    } else {
      p.set('from', getFromDate(dateRange))
    }
    if (search) p.set('q', search)
    p.set('sortCol', sort.col)
    p.set('sortDir', sort.dir)
    p.set('pageSize', String(pageSize))
    p.set('page', String(page))
    return p
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.join(','), sparkId.join(','), action.join(','), actorSub.join(','), dateRange, dateFrom, dateTo, page, search, sort.col, sort.dir, pageSize])

  /* ─── Stats query ─── */
  const statsQuery = useQuery({
    queryKey: ['audit-log', 'stats'],
    queryFn: () => apiFetch<AuditLogStats>('/api/v1/audit-log/stats'),
    // Append-only live feed: the app-wide `staleTime` would otherwise hide the
    // entry a user just generated elsewhere in Studio for its whole window.
    staleTime: 0,
    retry: false,
  })

  const accessDenied = is403(statsQuery.error)

  /* ─── Entries query ─── */
  const entriesQuery = useQuery({
    queryKey: ['audit-log', 'entries', apiParams.toString()],
    queryFn: () => apiFetch<AuditLogListResponse>(`/api/v1/audit-log?${apiParams.toString()}`),
    staleTime: 0,
    enabled: !accessDenied && !statsQuery.isLoading,
  })

  /* ─── Sparks for dropdown ─── */
  const sparksQuery = useQuery({
    queryKey: ['agents', 'audit-filter'],
    queryFn: () => apiFetch<SparkListResponse>('/api/v1/agents?limit=100'),
    enabled: !accessDenied,
  })

  const displayedEntries = entriesQuery.data?.items ?? []

  /* ─── Actors for dropdown (every actor with an audit-log entry, not just the current page) ─── */
  const actorsQuery = useQuery({
    queryKey: ['audit-log', 'actors'],
    queryFn: () => apiFetch<AuditLogActorsResponse>('/api/v1/audit-log/actors'),
    staleTime: 0,
    enabled: !accessDenied,
  })

  const uniqueActors = actorsQuery.data?.items ?? []

  const closeDrawer = useCallback(() => setSelectedEntry(null), [])

  /* ─── CSV export ─── */
  async function doExport() {
    const params = new URLSearchParams(apiParams)
    params.delete('page')
    params.delete('pageSize')
    try {
      const response = await fetch(`/api/v1/audit-log/export.csv?${params.toString()}`, {
        credentials: isAuthenticated ? 'include' : 'same-origin',
      })
      if (!response.ok) return
      const blob = await response.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = 'audit-log.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(href)
    } catch {
      /* download failed — no PII to log */
    }
  }

  function handleExport() {
    const data = entriesQuery.data
    if (data && data.totalCount > data.items.length) {
      setShowExportWarning(true)
      return
    }
    void doExport()
  }

  async function handleSnapshot() {
    setSnapshotLoading(true)
    try {
      const result = await apiFetch<{ downloadUrl: string }>('/api/v1/audit-log/incident-snapshot', {
        method: 'POST',
      })
      const a = document.createElement('a')
      a.href = result.downloadUrl
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch {
      /* error surfaced via snapshotLoading reset */
    } finally {
      setSnapshotLoading(false)
    }
  }

  /* ─── Loading state ─── */
  if (statsQuery.isLoading) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 32px 32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner />
      </div>
    )
  }

  /* ─── Access denied (403) ─── */
  if (accessDenied) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 32px 32px' }}>
        <PageHeader title={t('auditLog.heading')} caption={t('auditLog.subtitle')} />
        <div role="alert" style={{
          background: 'var(--danger-bg)', border: '1px solid var(--danger)',
          borderRadius: 8, padding: '16px 20px', fontSize: 13, color: 'var(--danger)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <InfoIcon size={18} />
          <div>
            {t('auditLog.accessDenied')}
          </div>
        </div>
      </div>
    )
  }

  const stats = statsQuery.data
  const sparks = sparksQuery.data?.items ?? []
  const totalPages = entriesQuery.data?.totalPages ?? 0

  let tableStateLabel: 'loading' | 'error' | 'empty' | 'ready'
  if (entriesQuery.isLoading) {
    tableStateLabel = 'loading'
  } else if (entriesQuery.isError) {
    tableStateLabel = 'error'
  } else if (displayedEntries.length === 0) {
    tableStateLabel = 'empty'
  } else {
    tableStateLabel = 'ready'
  }

  return (
    <div className="page-view">
      <div className="page-header">
        <PageHeader title={t('auditLog.heading')} caption={t('auditLog.subtitle')} />

        {/* Tab bar */}
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'configuration'}
            onClick={() => setActiveTab('configuration')}
            className={`tab-btn ${activeTab === 'configuration' ? 'active' : ''}`}
          >
            {t('auditLog.tabs.configuration')}
          </button>
          {FEATURES.auditLogInteractionsTab && (
            <button
              type="button"
              role="tab"
              aria-selected={false}
              disabled
              aria-disabled="true"
              className="tab-btn"
              style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}
            >
              {t('auditLog.tabs.sparkInteractions')}{' '}
              <span className="nav-badge-soon" title={t('common.previewTooltip')}>
                {t('common.preview')}
              </span>
            </button>
          )}
        </div>
      </div>{/* end .page-header */}

      <div className="page-body audit-log-body">
      {/* Tab panel: Configuration */}
      <div role="tabpanel">
        {activeTab === 'configuration' && (
          <>
            {/* Persistent filter bar — sticky while the table scrolls beneath it */}
            <div className="filter-card">
              <div className="filter-fields">
                <div className="filter-field" style={{ flex: '1.5 1 0' }}>
                  <div className="filter-eraser-row">
                    <label htmlFor="al-search">{t('auditLog.filters.searchLabel')}</label>
                    <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => setFilter('search', '')} />
                  </div>
                  <input
                    id="al-search"
                    type="text"
                    placeholder={t('auditLog.filters.search')}
                    value={search}
                    onChange={e => setFilter('search', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="filter-field">
                  <div className="filter-eraser-row">
                    <label>{t('auditLog.filters.user')}</label>
                    <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => setMultiFilter('actorSub', [])} />
                  </div>
                  <MultiSelectFilter
                    options={uniqueActors.map(a => ({ value: a.sub, label: a.displayName }))}
                    selected={actorSub}
                    onChange={v => setMultiFilter('actorSub', v)}
                    placeholder={t('auditLog.filters.allUsers')}
                    ariaLabel={t('auditLog.filters.user')}
                  />
                </div>

                <div className="filter-field">
                  <div className="filter-eraser-row">
                    <label>{t('auditLog.filters.scope')}</label>
                    <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => setMultiFilter('scope', [])} />
                  </div>
                  <MultiSelectFilter
                    options={SCOPES.map(s => ({ value: s, label: t(scopeI18nKey(s)) }))}
                    selected={scope}
                    onChange={v => setMultiFilter('scope', v)}
                    placeholder={t('auditLog.filters.allScopes')}
                    ariaLabel={t('auditLog.filters.scope')}
                  />
                </div>

                <div className="filter-field">
                  <div className="filter-eraser-row">
                    <label>{t('auditLog.filters.spark')}</label>
                    <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => setMultiFilter('agentId', [])} />
                  </div>
                  <MultiSelectFilter
                    options={sparks.map(s => ({ value: s.id, label: s.displayName }))}
                    selected={sparkId}
                    onChange={v => setMultiFilter('agentId', v)}
                    placeholder={t('auditLog.filters.allSparks')}
                    ariaLabel={t('auditLog.filters.spark')}
                  />
                </div>

                <div className="filter-field">
                  <div className="filter-eraser-row">
                    <label>{t('auditLog.filters.action')}</label>
                    <FilterEraser label={t('auditLog.filters.clearFilter')} onClick={() => setMultiFilter('action', [])} />
                  </div>
                  <MultiSelectFilter
                    options={ACTIONS.map(a => ({ value: a, label: a }))}
                    selected={action}
                    onChange={v => setMultiFilter('action', v)}
                    placeholder={t('auditLog.filters.allActions')}
                    ariaLabel={t('auditLog.filters.action')}
                  />
                </div>

                <div className="filter-field">
                  <div className="filter-eraser-row">
                    <label>{t('auditLog.filters.dateRange')}</label>
                    <FilterEraser
                      label={t('auditLog.filters.clearFilter')}
                      onClick={() => setDateRange({ start: null, end: null, periodLabel: periodLabelByKey[DEFAULT_PERIOD_KEY] })}
                    />
                  </div>
                  <DateRangeFilter
                    periods={periodOptions.map(({ label, days }) => ({ label, days }))}
                    value={dateRangeValue}
                    onChange={setDateRange}
                    ariaLabel={t('auditLog.filters.dateRange')}
                  />
                </div>
              </div>
            </div>

            {/* Configuration Changes card */}
            <div className="card" style={{ marginBottom: 0 }}>
              {/* Card header */}
              <div className="card-header">
                <div>
                  <h3>{t('auditLog.configChanges.title')}</h3>
                  <p>{t('auditLog.configChanges.subtitle')}</p>
                </div>
                <div className="card-header-actions">
                  {FEATURES.auditLogIncidentSnapshot && isGlobalAdmin && (
                    <button
                      type="button"
                      onClick={() => void handleSnapshot()}
                      disabled={snapshotLoading}
                      className="btn btn-ghost"
                      style={{ fontSize: 12, padding: '5px 12px' }}
                      aria-label={t('auditLog.incidentSnapshot')}
                    >
                      {snapshotLoading ? t('common.loading') : t('auditLog.incidentSnapshot')}
                    </button>
                  )}
                  {isGlobalAdmin && (
                    <button
                      type="button"
                      onClick={handleExport}
                      className="btn btn-ghost"
                      style={{ fontSize: 12, padding: '5px 12px' }}
                    >
                      {t('auditLog.exportCsv')}
                    </button>
                  )}
                </div>
              </div>

              {/* KPI tiles + Table */}
              <div className="card-body">
                <div className="kpi-row" data-testid="audit-kpi-row">
                  <KpiTile
                    value={stats ? String(stats.changesLast30Days) : '—'}
                    label={t('auditLog.stats.changesLast30')}
                  />
                  <KpiTile
                    value={stats ? String(stats.activeUsers) : '—'}
                    label={t('auditLog.stats.activeUsers')}
                  />
                  <KpiTile
                    value={formatRelative(stats?.lastChangeAt ?? null)}
                    label={t('auditLog.stats.lastChange')}
                  />
                  <KpiTile
                    value={stats?.mostChangedAgent?.displayName ?? stats?.mostChangedSpark?.displayName ?? '—'}
                    label={t('auditLog.stats.mostChanged')}
                  />
                </div>

                {/* Pagination — the shared toolbar (MS-699), now with server-side sort
                    and a configurable page size (MS-737 AC2/AC3). Sits above the table,
                    matching the prototype and MCP Analytics → Tool Invocations. */}
                {entriesQuery.data && displayedEntries.length > 0 && (
                  <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalCount={entriesQuery.data.totalCount}
                    totalPages={totalPages}
                    itemsOnPage={displayedEntries.length}
                    onPageChange={setPage}
                    pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
                    onPageSizeChange={handlePageSizeChange}
                    onResetSort={() => setSort(DEFAULT_SORT)}
                    isSortDefault={sort.col === DEFAULT_SORT.col && sort.dir === DEFAULT_SORT.dir}
                    renderCount={({ from, to, total }) => t('auditLog.pager.showing', { from, to, total })}
                    emptyLabel={t('auditLog.pager.empty')}
                  />
                )}

                {tableStateLabel === 'loading' && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                    <Spinner />
                  </div>
                )}
                {tableStateLabel === 'error' && (
                  <div role="alert" style={{ padding: '16px 0', color: 'var(--danger)', fontSize: 13 }}>
                    {t('auditLog.failedToLoad')}{' '}
                    {entriesQuery.error instanceof Error ? entriesQuery.error.message : ''}
                  </div>
                )}
                {tableStateLabel === 'empty' && (
                  <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                    {t('auditLog.noEntries')}
                  </div>
                )}
                {tableStateLabel === 'ready' && (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '2%' }}>
                          <SeverityInfoIcon />
                        </th>
                        {COLUMNS.map(column => (
                          <SortableTh
                            key={column.key}
                            colKey={column.key}
                            label={t(column.labelKey)}
                            sort={sort}
                            onSort={handleSort}
                            defaultsToDescending={column.descendingFirst}
                            width={column.width}
                          />
                        ))}
                        <th style={{ width: '5%' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {displayedEntries.map((entry) => {
                        return (
                          <tr
                            key={entry.id}
                            onClick={() => setSelectedEntry(entry)}
                            className="audit-clickable"
                          >
                            <td>
                              <SeverityDot severity={entry.severity} />
                            </td>
                            <td style={{ whiteSpace: 'nowrap', color: 'var(--text-tertiary)', fontSize: 13 }}>
                              {formatTimestamp(entry.occurredAt)}
                            </td>
                            <td>
                              <ActorCell entry={entry} />
                            </td>
                            <td>
                              <ScopeBadge scope={entry.resourceType} />
                            </td>
                            <td style={{ fontSize: 13 }}>
                              {entry.resourceDisplayName ?? '—'}
                            </td>
                            <td className="audit-section-cell">
                              {entry.section ?? '—'}
                            </td>
                            <td>
                              <ActionBadge action={entry.action} />
                            </td>
                            <td
                              title={entry.changeDescription}
                              style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {entry.changeDescription}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setSelectedEntry(entry) }}
                                aria-label={`Show details for entry at ${formatTimestamp(entry.occurredAt)}`}
                                className="btn btn-ghost audit-details-btn"
                              >
                                {t('auditLog.table.showDetails')}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      </div>{/* end .page-body */}

      {/* Detail drawer */}
      <AuditDetailDrawer entry={selectedEntry} onClose={closeDrawer} />

      {/* Large-export warning */}
      {showExportWarning && (
        <ConfirmDialog
          title={t('auditLog.exportWarning.title')}
          message={t('auditLog.exportWarning.body')}
          confirmLabel={t('auditLog.exportWarning.confirm')}
          cancelLabel={t('auditLog.exportWarning.cancel')}
          onConfirm={() => { setShowExportWarning(false); void doExport() }}
          onCancel={() => setShowExportWarning(false)}
        />
      )}
    </div>
  )
}
